/**
 * The FE-canonical label for a data point whose underlying value is SQL NULL.
 * Use this wherever a null breakdown value or filter option is shown to the user,
 * so the label can be changed globally in one place.
 */
export const ALIAS_FOR_NULL_VALUES = 'No Value'

/**
 * All FE-fabricated strings that mean "this row's wire value is SQL NULL".
 * Anything matching here must round-trip as IS_NULL, never as an EQ filter.
 */
const NULL_VALUE_ALIASES = new Set([
  '',
  'null',
  'none',
  'unknown',
  'n/a',
  'na',
  'undefined',
  ALIAS_FOR_NULL_VALUES.toLowerCase(), // 'no value'
])

/**
 * Returns true when `value` is a known FE null-value alias and must be
 * treated as IS_NULL rather than an equality filter against the literal string.
 */
export const isNullValueAlias = (value: string): boolean =>
  NULL_VALUE_ALIASES.has(value.trim().toLowerCase())

/**
 * The FE-canonical label for a selected filter value that the current option
 * set no longer offers — a cluster or category removed by a project update.
 *
 * Distinct from `ALIAS_FOR_NULL_VALUES` on purpose: that one means the datum
 * IS null and must round-trip as IS_NULL, whereas this one stands in for a
 * real value whose name can no longer be looked up. It is display-only —
 * callers keep the original value as the row's identifier so removing it
 * writes back the stored value, never this string.
 */
export const ALIAS_FOR_DELETED_VALUES = '[Deleted Value]'
